import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { ESearchParams, TSearchParams, TSearchParamsMap } from '@/types/searchParams';

type TSetSearchParams = (params: TSearchParams, options?: { replace?: boolean }) => void;

const useURLSearchParams = (): [
	searchParams: TSearchParamsMap,
	setSearchParams: TSetSearchParams,
] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const searchParamsMap: TSearchParamsMap = useMemo(() => {
		const result = {} as TSearchParamsMap;

		for (const [key, value] of Array.from(searchParams.entries())) {
			result[key as ESearchParams] = value;
		}

		return result;
	}, [searchParams]);

	const onSetSearchParams: TSetSearchParams = useCallback(
		(params, { replace = false } = {}) => {
			if (!Object.keys(params).length) {
				setSearchParams({});

				return;
			}

			for (const [key, value] of Object.entries(params)) {
				if (value) {
					searchParams.set(key, value.toString());
				} else {
					searchParams.delete(key);
				}
			}

			setSearchParams(searchParams, { replace });
		},
		[searchParams, setSearchParams],
	);

	return [searchParamsMap, onSetSearchParams];
};

export default useURLSearchParams;
